// Update your App.jsx to include admin routes
import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/common/ProtectedRoute';

// Lazy load pages to break cycles
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const SignupPage = React.lazy(() => import('./pages/Signup'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const ProfilePage = React.lazy(() => import('./pages/Profile'));
const Restaurants = React.lazy(() => import('./pages/Restaurants'));
const RestaurantDetail = React.lazy(() => import('./pages/RestaurantDetail'));
const MealPlanning = React.lazy(() => import('./pages/MealPlanning'));
const Recipes = React.lazy(() => import('./pages/Recipes'));
const RecipeDetail = React.lazy(() => import('./pages/RecipeDetail'));
const Shopping = React.lazy(() => import('./pages/Shopping'));
const ShoppingCart = React.lazy(() => import('./pages/ShoppingCart'));
const ShoppingFavorites = React.lazy(() => import('./pages/ShoppingFavorites'));
const Rewards = React.lazy(() => import('./pages/Rewards'));
const Community = React.lazy(() => import('./pages/Community'));


// Admin pages
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminRestaurants = React.lazy(() => import('./pages/admin/AdminRestaurants'));
const AdminStores = React.lazy(() => import('./pages/admin/AdminStores'));
const AdminSettings = React.lazy(() => import('./pages/admin/AdminSettings'));
const AdminLogin = React.lazy(() => import('./pages/admin/AdminLogin'));

// Owner pages
const OwnerLogin = React.lazy(() => import('./pages/owner/OwnerLogin'));
const RestaurantDashboard = React.lazy(() => import('./pages/owner/RestaurantDashboard'));
const StoreDashboard = React.lazy(() => import('./pages/owner/StoreDashboard'));

// Theme configuration (keep your existing theme)
const theme = createTheme({
  palette: {
    primary: {
      main: '#3AB795',
      light: '#5FC7A7',
      dark: '#2A8F75',
    },
    secondary: {
      main: '#FFB703',
      light: '#FFC83A',
      dark: '#E6A500',
    },
    accent: {
      main: '#219EBC',
      light: '#4AB3CE',
      dark: '#1A7E96',
    },
    background: {
      default: '#F4F9FF',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1C1C1C',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
  },
});

// Loading component
function LoadingSpinner() {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}
    >
      Loading...
    </div>
  );
}

// Admin Protected Route component
function AdminRoute({ children }) {
  return (
    <ProtectedRoute adminOnly={true}>
      {children}
    </ProtectedRoute>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <React.Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/admin/login" element={<AdminLogin />} />

              
              {/* User Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile/edit"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/restaurants"
                element={
                  <ProtectedRoute>
                    <Restaurants />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/restaurants/:id"
                element={
                  <ProtectedRoute>
                    <RestaurantDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/meal-planning"
                element={
                  <ProtectedRoute>
                    <MealPlanning />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/recipes"
                element={
                  <ProtectedRoute>
                    <Recipes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/recipes/:id"
                element={
                  <ProtectedRoute>
                    <RecipeDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/shopping"
                element={
                  <ProtectedRoute>
                    <Shopping />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/shopping/cart"
                element={
                  <ProtectedRoute>
                    <ShoppingCart />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/shopping/favorites"
                element={
                  <ProtectedRoute>
                    <ShoppingFavorites />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rewards"
                element={
                  <ProtectedRoute>
                    <Rewards />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/community"
                element={
                  <ProtectedRoute>
                    <Community />
                  </ProtectedRoute>
                }
              />
              
              {/* Admin Protected Routes */}
              
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/dashboard"
                element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/restaurants"
                element={
                  <AdminRoute>
                    <AdminRestaurants />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/restaurants/:id"
                element={
                  <AdminRoute>
                    <AdminRestaurants />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/stores"
                element={
                  <AdminRoute>
                    <AdminStores />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/stores/:id"
                element={
                  <AdminRoute>
                    <AdminStores />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <AdminRoute>
                    <AdminSettings />
                  </AdminRoute>
                }
              />
              {/* Owner Routes */}
              <Route path="/owner/login" element={<OwnerLogin />} />
              <Route path="/owner/restaurant/dashboard" element={<RestaurantDashboard />} />
              <Route path="/owner/store/dashboard" element={<StoreDashboard />} />

            </Routes>
          </React.Suspense>
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;